import React from 'react';
import { ResponsiveContainer, RadialBarChart, RadialBar, Cell, PolarAngleAxis } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';

const COLOR_MAP = {
  default: 'hsl(210, 55%, 40%)',
  primary: 'hsl(210, 55%, 40%)',
  secondary: 'hsl(260, 35%, 45%)',
  success: 'hsl(160, 45%, 35%)',
  warning: 'hsl(38, 55%, 45%)',
  danger: 'hsl(4, 65%, 45%)',
};

const BG_MAP = {
  default: 'hsl(210, 15%, 93%)',
  primary: 'hsl(210, 15%, 93%)',
  secondary: 'hsl(260, 15%, 93%)',
  success: 'hsl(160, 15%, 93%)',
  warning: 'hsl(38, 15%, 93%)',
  danger: 'hsl(4, 15%, 93%)',
};

export function CircleChartCard({ title, color = 'default', chartData, total, className }) {
  const fillColor = COLOR_MAP[color] || COLOR_MAP.default;
  const bgColor = BG_MAP[color] || BG_MAP.default;

  const data = chartData?.length ? chartData : [{ name: title, value: 0 }];

  return (
    <Card className={cn('border-[#E7E0D6] dark:border-white/[0.06] bg-white dark:bg-[#1E2025] h-[240px]', className)}>
      <CardContent className="flex flex-col h-full pt-4 pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-[#5B616E] dark:text-white/60">{title}</h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              <DropdownMenuItem className="text-xs">View Details</DropdownMenuItem>
              <DropdownMenuItem className="text-xs">Export Data</DropdownMenuItem>
              <DropdownMenuItem className="text-xs">Set Alert</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              barSize={10}
              cx="50%"
              cy="50%"
              data={data}
              endAngle={-270}
              innerRadius={90}
              outerRadius={70}
              startAngle={90}
            >
              <PolarAngleAxis angleAxisId={0} domain={[0, total]} tick={false} type="number" />
              <RadialBar
                angleAxisId={0}
                animationDuration={1000}
                animationEasing="ease"
                background={{ fill: bgColor }}
                cornerRadius={12}
                dataKey="value"
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={fillColor} />
                ))}
              </RadialBar>
              <g>
                <text textAnchor="middle" x="50%" y="48%">
                  <tspan className="fill-[#5B616E] dark:fill-white/50 text-xs" dy="-0.5em" x="50%">
                    {data[0]?.name}
                  </tspan>
                  <tspan className="fill-[#121316] dark:fill-white/90 text-sm font-semibold" dy="1.5em" x="50%">
                    {total?.toLocaleString() ?? '0'}
                  </tspan>
                </text>
              </g>
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export function CircleChartGrid({ items }) {
  return (
    <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((item, index) => (
        <CircleChartCard key={index} {...item} />
      ))}
    </div>
  );
}
